import ButtonCircle from '../../../shared/components/button_circle';
import './styles.scss';
import { mainSlice, useDispatch } from '../../../stateManagement/redux';
import {
    Component,
    useCallback,
    useEffect,
    useRef,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';
import { consolidateFilesLocal, getLocationsSimpleList } from '../../../stateManagement/redux/slices/gerencia_tecnica/asyncReducers';
import { FileCustom, UploadFileProps } from './interfaces';
import { UploadModal } from "./upload-modal";
import { useNavigate } from 'react-router-dom';
import { originalFlowUploadV1 } from '../../../stateManagement/redux/slices/gerencia_tecnica/interfaces';
import { ConsolidateFileDropSection, toFileCustom } from './ConsolidateFileDropSection';

const EXCEL_ACCEPT = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel": [".xls"],
};

const TXT_ACCEPT = { "text/plain": [".txt"] };

const TAM_ACCEPT = {
    "application/zip": [".zip", ".TAM_EXP", ".tam"],
    "application/octet-stream": [".TAM_EXP", ".tam"],
};

type ConsolidatePayload = {
    excel: FileCustom[];
    echometer: FileCustom[];
    siam: FileCustom[];
    flow: string;
};

class UploadModalErrorBoundary extends Component<
    { onClose: () => void; children: ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: unknown) {
        console.error('UploadModal render error:', error);
        this.props.onClose();
    }

    render() {
        if (this.state.hasError) return null;
        return this.props.children;
    }
}

function toast(dispatch: ReturnType<typeof useDispatch>, message: string, type: 'error' | 'info' | 'success' = 'error') {
    dispatch(mainSlice.actions.addToastNotification({
        id: `${Date.now()}`,
        message,
        type,
        duration: 5000,
    }));
}

export const ConsolidateFileModule = (props: UploadFileProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getLocationsSimpleList());
    }, [dispatch]);

    const [filesExcel, setFilesExcel] = useState<FileCustom[]>([]);
    const [filesEchometer, setFilesEchometer] = useState<FileCustom[]>([]);
    const [filesSiam, setFilesSiam] = useState<FileCustom[]>([]);
    const [filesTam, setFilesTam] = useState<FileCustom[]>([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [originalFlow, setOriginalFlow] = useState<string>("");

    const consolidatePayloadRef = useRef<ConsolidatePayload | null>(null);

    const appendFiles = useCallback(
        (setter: Dispatch<SetStateAction<FileCustom[]>>, typeOfFile: FileCustom["typeOfFile"]) =>
            (accepted: File[]) => {
                setter((prev) => [...prev, ...accepted.map((f) => toFileCustom(f, typeOfFile))]);
            },
        [],
    );

    const totalFiles = filesExcel.length + filesEchometer.length + filesSiam.length + filesTam.length;
    const canConsolidate = filesExcel.length > 0 && Boolean(originalFlow);

    const closeModal = useCallback(() => {
        setShowUploadModal(false);
        consolidatePayloadRef.current = null;
    }, []);

    const startUploadFiles = () => {
        if (filesExcel.length === 0) {
            toast(dispatch, 'Debes subir al menos un archivo Excel');
            return;
        }

        if (!originalFlow) {
            toast(dispatch, 'Debes seleccionar un tipo de Tecnología');
            return;
        }

        consolidatePayloadRef.current = {
            excel: filesExcel,
            echometer: filesEchometer,
            siam: filesSiam,
            flow: originalFlow,
        };
        setShowUploadModal(true);
    };

    useEffect(() => {
        if (!showUploadModal || !consolidatePayloadRef.current) return;

        const { excel, echometer, siam, flow } = consolidatePayloadRef.current;
        dispatch(consolidateFilesLocal(excel, echometer, siam, flow));
    }, [showUploadModal, dispatch]);

    const consolidateHint = filesExcel.length === 0
        ? 'Sube al menos un archivo Excel para consolidar'
        : !originalFlow
            ? 'Selecciona un tipo de tecnología para consolidar'
            : 'Consolidar archivos cargados';

    return (
        <>
            <div className='Upload_files_container Upload_files_container--v1'>
                <div className='Upload_files_container_header'>
                    <ButtonCircle icon="/assets/icons/back.svg" iconHeight={50} iconWidth={50} onClick={() => navigate('/gto/upload')} />
                    <div className='Upload_files_container_title'>{props.title}</div>
                </div>

                <div className="Upload_files_container_body">
                    <div className="Upload_files_container_toolbar">
                        <div className="Upload_files_container_toolbar_left">
                            <label className="Upload_files_container_form_section_select">
                                <span>Tipo de Tecnología</span>
                                <select
                                    id="activity"
                                    name="activity"
                                    value={originalFlow}
                                    onChange={(e) => setOriginalFlow(e.target.value)}
                                >
                                    <option value="">Selecciona una opción</option>
                                    {originalFlowUploadV1.map((f) => (
                                        <option key={f.id} value={f.id}>{f.name}</option>
                                    ))}
                                </select>
                            </label>

                            {totalFiles > 0 && (
                                <span className="Upload_files_container_file_summary">
                                    {totalFiles} archivo{totalFiles !== 1 ? 's' : ''} seleccionado{totalFiles !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>

                        <div className="Upload_files_container_toolbar_right">
                            <p className="Upload_files_container_toolbar_hint">
                                Excel es obligatorio. Echometer, SIAM y TAM son opcionales; los TAM se validan en la pestaña TAM del modal.
                            </p>
                            <button
                                type="button"
                                className={`Upload_files_container_consolidate_btn${canConsolidate ? '' : ' Upload_files_container_consolidate_btn--disabled'}`}
                                onClick={startUploadFiles}
                                title={consolidateHint}
                                aria-disabled={!canConsolidate}
                            >
                                <img src="/assets/icons/merge.svg" alt="" width={18} height={18} />
                                Consolidar
                            </button>
                        </div>
                    </div>

                    <div className='Upload_files_container_field_container'>
                        <ConsolidateFileDropSection
                            title="Archivos Excel"
                            hint="Arrastra reportes .xlsx / .xls"
                            formats={['.xlsx', '.xls']}
                            files={filesExcel}
                            onAdd={appendFiles(setFilesExcel, "excel")}
                            onRemove={(f) => setFilesExcel((prev) => prev.filter((x) => x !== f))}
                            accept={EXCEL_ACCEPT}
                            required
                            size="compact"
                        />
                        <ConsolidateFileDropSection
                            title="Archivos TAM"
                            hint="Estudios .TAM / .TAM_EXP"
                            formats={['.TAM_EXP', '.tam', '.zip']}
                            files={filesTam}
                            onAdd={appendFiles(setFilesTam, "tam")}
                            onRemove={(f) => setFilesTam((prev) => prev.filter((x) => x !== f))}
                            accept={TAM_ACCEPT}
                            optional
                            size="compact"
                        />
                        <ConsolidateFileDropSection
                            title="Archivos Echometer (.txt)"
                            hint="Cartas dinagráficas Echometer"
                            formats={['.txt']}
                            files={filesEchometer}
                            onAdd={appendFiles(setFilesEchometer, "echometer")}
                            onRemove={(f) => setFilesEchometer((prev) => prev.filter((x) => x !== f))}
                            onClear={() => setFilesEchometer([])}
                            accept={TXT_ACCEPT}
                            size="expanded"
                        />
                        <ConsolidateFileDropSection
                            title="Archivos SIAM"
                            hint="Estudios SIAM en .txt"
                            formats={['.txt']}
                            files={filesSiam}
                            onAdd={appendFiles(setFilesSiam, "siam")}
                            onRemove={(f) => setFilesSiam((prev) => prev.filter((x) => x !== f))}
                            accept={TXT_ACCEPT}
                            size="expanded"
                        />
                    </div>
                </div>
            </div>

            {showUploadModal && createPortal(
                <div
                    className="consolidate_files_container_external_container"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeModal();
                    }}
                    role="presentation"
                >
                    <UploadModalErrorBoundary onClose={closeModal}>
                        <UploadModal
                            close={closeModal}
                            originalFlow={originalFlow}
                            tamFiles={filesTam}
                        />
                    </UploadModalErrorBoundary>
                </div>,
                document.body,
            )}
        </>
    );
};
